/**
 * Initialize plots for a game level
 * Creates plot data structure with terrain types
 */

import type { Plot } from '@/types';
import { generateLevel1Hexes } from './hexGrid';

/**
 * Terrain distribution for Level 1 (Lunar Outpost)
 * Based on LEVEL_DESIGN.md and M.U.L.E. mechanics:
 * - Mare (Plains) - 30% - Good for food, helium-3
 * - Highlands - 25% - Good for energy
 * - Craters - 20% - Good for food, titanium, helium-3 (water pockets in shadows)
 * - Mountains - 15% - Good for energy, titanium
 * - Regolith Fields - 10% - Good for helium-3
 * - Base - Starting outpost (1-2 hexes at center)
 * 
 * For ~37 hexes (circular map):
 * - Mare: ~11 hexes (30%)
 * - Highlands: ~9 hexes (25%)
 * - Craters: ~7 hexes (20%)
 * - Mountains: ~6 hexes (15%)
 * - Regolith: ~4 hexes (10%)
 * - Base: 1-2 hexes
 */
const LEVEL_1_TERRAIN_DISTRIBUTION = [
  // Mare (Plains) - 30% - good for food and helium-3
  'lunar', 'lunar', 'lunar', 'lunar', 'lunar', 'lunar', 'lunar', 'lunar', 'lunar', 'lunar', 'lunar',
  // Highlands - 25% - good for energy
  'highland', 'highland', 'highland', 'highland', 'highland', 'highland', 'highland', 'highland', 'highland',
  // Craters - 20% - good for food, titanium, helium-3 (water pockets in shadows)
  'crater', 'crater', 'crater', 'crater', 'crater', 'crater', 'crater',
  // Mountains - 15% - good for energy and titanium
  'mountain', 'mountain', 'mountain', 'mountain', 'mountain',
  // Regolith Fields - 10% - good for helium-3
  'regolith', 'regolith', 'regolith',
  // Base (starting outpost) - 1-2 hexes at center
  'base', 'base',
];

/**
 * Initialize plots for Level 1 (Lunar Outpost)
 * Creates 19 plots with terrain types
 * Uses deterministic assignment based on hex position for consistency
 */
export function initializeLevel1Plots(): Plot[] {
  const hexCoords = generateLevel1Hexes();
  const plots: Plot[] = [];
  
  // Deterministic hash function for terrain assignment
  // This ensures the same hex always gets the same terrain
  const getTerrainIndex = (q: number, r: number, total: number): number => {
    const hash = Math.abs((q * 31 + r * 17) % total);
    return hash;
  };
  
  // Find center hex (0, 0) for base placement
  hexCoords.forEach((hex) => {
    // Place base at center (0, 0) - the starting outpost
    if (hex.q === 0 && hex.r === 0) {
      plots.push({
        plotId: `plot_${hex.q}_${hex.r}`,
        x: hex.q,
        y: hex.r,
        terrain: 'base',
      });
    } else {
      // Assign terrain based on deterministic hash
      const terrainIndex = getTerrainIndex(hex.q, hex.r, LEVEL_1_TERRAIN_DISTRIBUTION.length);
      plots.push({
        plotId: `plot_${hex.q}_${hex.r}`,
        x: hex.q,
        y: hex.r,
        terrain: LEVEL_1_TERRAIN_DISTRIBUTION[terrainIndex] as Plot['terrain'],
      });
    }
  });
  
  return plots;
}

/**
 * Initialize plots for any level
 */
export function initializePlots(levelId: string): Plot[] {
  switch (levelId) {
    case 'lunar':
      return initializeLevel1Plots();
    // Add other levels as they're implemented
    default:
      return initializeLevel1Plots(); // Default to Level 1
  }
}

